import React, { useCallback, useContext, useState } from 'react';
import { useStore, getBezierPath, EdgeLabelRenderer } from 'reactflow';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CanvasContext } from '../../WorkflowCanvas';
import { createSaveEvent } from '../../../../../utils/createSaveEvent';
import { saveCanvasEvent } from '../../../../../utils/canvasApi';
import useProjectStore from '../../../../ProjectCanvas/useProjectStore';
import { getElements } from '../../../../../utils/CanvasUtil';
import useCanvasStore from '../../useCanvasStore';
import usePlaceholderTraceStore from '../../../../Placeholders/usePlaceholderTraceStore';

const GatewayLink = ({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected }) => {
  const edges = useCanvasStore(s => s.edges);
  const isTraced = usePlaceholderTraceStore(s => s.traceEdgeIds.has(id));
  const [isHovered, setIsHovered] = useState(false);

  const { modeInfo, canvasInstance, savingInfo, updated } = useContext(CanvasContext);
  const [mode] = modeInfo || [null];
  const [reactFlowInstance] = canvasInstance || [null];
  const [saving, setSaving] = savingInfo || [false, () => {}];
  const [lastModified, setLastModified] = updated || [null, () => {}];

  const edgeCondition = data?.edgeCondition || {};
  const isDefault = edgeCondition.isDefault === true;

  const OPERATOR_LABELS = {
    eq: '=', neq: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=',
    contains: 'contains', not_contains: 'not contains',
    empty: 'is empty', not_empty: 'is not empty',
    starts_with: 'starts with', ends_with: 'ends with',
  };

  const buildLabel = () => {
    if (isDefault) return 'Default';
    const rules = edgeCondition.rules;
    if (!rules || rules.length === 0) return '';
    return rules.map((r, i) => {
      const field = r.field || '?';
      const op = OPERATOR_LABELS[r.operator] || r.operator || '?';
      const needsValue = r.operator !== 'empty' && r.operator !== 'not_empty';
      const val = needsValue ? ` ${r.value || '?'}` : '';
      const prefix = i > 0 ? ` ${(r.combinator || 'and').toUpperCase()} ` : '';
      return `${prefix}${field} ${op}${val}`;
    }).join('');
  };

  const label = buildLabel();

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (saving || mode !== 'DEFAULT') return;

    setSaving(true);
    const result = edges.filter(edge => edge.id !== id);
    useCanvasStore.getState().setEdges(result);

    if (!reactFlowInstance) {
      setSaving(false);
      return;
    }

    const rfiObj = reactFlowInstance.toObject();
    const viewport = {
      x: rfiObj.viewport.x.toFixed(5),
      y: rfiObj.viewport.y.toFixed(5),
      zoom: rfiObj.viewport.zoom.toFixed(5),
    };

    const eventData = {
      sourceComponentId: source,
      targetComponentId: target,
      noteId: id,
    };

    const saveEvent = createSaveEvent(
      'DELETE_LINK',
      useProjectStore.getState().project_id,
      viewport,
      getElements(reactFlowInstance),
      eventData
    );

    try {
      const response = await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent);
      if (response?.data?.updated) {
        setLastModified(response.data.lastModified);
      }
    } catch (error) {
      console.error('Error deleting gateway link:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (mode !== 'DEFAULT') return;
    window.dispatchEvent(new CustomEvent('editGatewayEdge', {
      detail: { edgeId: id, source, target, edgeCondition }
    }));
  };

  const showControls = (selected || isHovered) && mode === 'DEFAULT';

  return (
    <>
      <path
        id={id}
        className={isTraced ? 'placeholder-traced-path' : undefined}
        d={edgePath}
        fill="none"
        stroke={isDefault ? '#999999' : '#F5A623'}
        strokeWidth={3}
        strokeDasharray={isDefault ? '5,5' : undefined}
        markerEnd={isDefault ? "url(#arrowhead-gateway-default)" : "url(#arrowhead-gateway)"}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      />

      <defs>
        <marker
          id="arrowhead-gateway"
          viewBox="0 0 10 10"
          refX="6"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0 0 L 7 5 L 0 10 z" fill="#F5A623" />
        </marker>
        <marker
          id="arrowhead-gateway-default"
          viewBox="0 0 10 10"
          refX="6"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0 0 L 7 5 L 0 10 z" fill="#999999" />
        </marker>
      </defs>

      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              style={{
                background: isDefault ? '#f5f5f5' : '#FFF3E0',
                border: `1px solid ${isDefault ? '#ccc' : '#F5A623'}`,
                borderRadius: 8,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 500,
                color: isDefault ? '#666' : '#E65100',
                maxWidth: 200,
                wordBreak: 'break-word',
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              {label}
            </div>
            {showControls && (
              <div style={{ display: 'flex', gap: 2 }}>
                <IconButton size="small" onClick={handleEdit} sx={{ borderRadius: '4px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', width: 24, height: 24, p: '4px' }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" onClick={handleDelete} sx={{ borderRadius: '4px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', width: 24, height: 24, p: '4px' }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default GatewayLink;
